import dayjs from "dayjs";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import type {
  PlaceMFOrderItem,
  PlaceMFOrderPayload,
} from "store/features/MFOrders";

export type MFOrderType = "purchase" | "redemption" | "switch";

export type MFTransactionType = "lumpsum" | "sip" | "swp" | "stp";

export type MFFrequency = "monthly" | "quarterly" | "half_yearly" | "yearly";

export type MFPaymentType = "netbanking" | "upi" | "mandate";

export type RightVisibleTable =
  | "SELECT_PORTFOLIO"
  | "ENTER_AMOUNT_FOLIO"
  | "PLACE_ORDER"
  | "ORDER_PLACED"
  | null;

export type MFOrderItem = {
  portfolioId: number | null;
  folioNumber: string | null;
  amount: number | null;
  units: number | null;
  redemptionType: "all" | null;
  switchType: "all" | null;
  paymentType: MFPaymentType | null;
  mandateId: string | null;
  sourceRefId: string | null;
  initiatedBy: string | null;
};

export const getInitialMFOrderItem = (): MFOrderItem => ({
  portfolioId: null,
  folioNumber: null,
  amount: null,
  units: null,
  redemptionType: null,
  switchType: null,
  paymentType: null,
  mandateId: null,
  sourceRefId: null,
  initiatedBy: null,
});

export type MFPortfolioSelectionFilter = {
  portfolio_group: number[];
  portfolio_value: number[];
};

export type MFOrderFormValues = {
  orderType: MFOrderType | "";
  transactionType: MFTransactionType | "";
  gcode: string;
  switchInGcode: string | null;
  frequency: MFFrequency | null;
  dayOfMonth: number | null;
  dayOfWeek: number | null;
  installments: number | null;
  isPerpetual: boolean;
  startDate: string | null;
  scheduledFor: string | null;
  rationale: string;
  items: MFOrderItem[];
  portfolioSelectionFilter: MFPortfolioSelectionFilter;
  extraThings: {
    rightVisibleTable: RightVisibleTable;
  };
};

export const getInitialMFOrderFormValues = (): MFOrderFormValues => ({
  orderType: "",
  transactionType: "",
  gcode: "",
  switchInGcode: null,
  frequency: null,
  dayOfMonth: null,
  dayOfWeek: null,
  installments: null,
  isPerpetual: false,
  startDate: null,
  scheduledFor: null,
  rationale: "",
  items: [],
  portfolioSelectionFilter: {
    portfolio_group: [],
    portfolio_value: [],
  },
  extraThings: {
    rightVisibleTable: null,
  },
});

const toNumericString = (value: number | null): string | null =>
  value != null && !Number.isNaN(value) ? value.toFixed(2) : null;

const toItemPayload = (item: MFOrderItem): PlaceMFOrderItem => ({
  portfolioId: item.portfolioId as number,
  folioNumber: item.folioNumber,
  amount: toNumericString(item.amount),
  units: toNumericString(item.units),
  redemptionType: item.redemptionType,
  switchType: item.switchType,
  paymentType: item.paymentType,
  mandateId: item.mandateId,
  sourceRefId: item.sourceRefId,
  initiatedBy: item.initiatedBy,
});

export const buildPlaceMFOrderPayload = (
  values: MFOrderFormValues,
  options?: {
    sendForExecution?: boolean;
    portfolioIds?: number[];
    whatsappPortfolios?: number[];
  },
): PlaceMFOrderPayload => {
  const isPlan =
    values.transactionType === "sip" ||
    values.transactionType === "swp" ||
    values.transactionType === "stp";
  const startDate =
    isPlan && !values.startDate
      ? dayjs().format("YYYY-MM-DD")
      : values.startDate;

  const validItems = values.items.filter((item) => item.portfolioId != null);
  const portfolioIds =
    options?.portfolioIds ??
    Array.from(new Set(validItems.map((item) => item.portfolioId as number)));

  // Purchase is amount-driven on the backend — units is display-only (used for
  // the amount<->units preview), so it's dropped from the payload.
  const isPurchase = values.orderType === "purchase";

  return {
    assetClass: "mf",
    orderType: values.orderType as MFOrderType,
    transactionType: values.transactionType as MFTransactionType,
    gcode: values.gcode,
    switchInGcode: values.switchInGcode,
    frequency: values.frequency,
    dayOfMonth: values.dayOfMonth,
    dayOfWeek: values.dayOfWeek,
    installments: values.installments,
    isPerpetual: values.isPerpetual,
    startDate,
    scheduledFor: values.scheduledFor,
    rationale: values.rationale,
    sendForExecution: options?.sendForExecution ?? true,
    portfolioIds,
    whatsappPortfolios: options?.whatsappPortfolios ?? [],
    items: validItems.map((item) => {
      const payloadItem = toItemPayload(item);
      if (isPurchase) payloadItem.units = null;
      return payloadItem;
    }),
  };
};

const useMFOrders = () => {
  const form = useForm<MFOrderFormValues>({
    defaultValues: getInitialMFOrderFormValues(),
  });

  console.log(form.getValues());

  return { form };
};

export const useWatchFields = () => {
  const { control } = useFormContext<MFOrderFormValues>();

  return {
    transactionField: useWatch({ control, name: "transactionType" }),
    orderType: useWatch({ control, name: "orderType" }),
    switchInGcode: useWatch({ control, name: "switchInGcode" }),
    schemeName: useWatch({ control, name: "gcode" }),
    isPerpetual: useWatch({ control, name: "isPerpetual" }),
    frequency: useWatch({ control, name: "frequency" }),
    installments: useWatch({ control, name: "installments" }),
    "extraThings.rightVisibleTable": useWatch({
      control,
      name: "extraThings.rightVisibleTable",
    }),
  };
};

export default useMFOrders;
